import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  TrendingUp,
  AutoAwesome,
  Add,
  Edit,
  Delete,
  CheckCircle,
  Warning,
} from '@mui/icons-material';
import { supabase } from '../../lib/supabaseClient';

interface SuggestedKPI {
  name: string;
  description: string;
}

interface KPIConfigurationProps {
  templateName: string;
  templateStructure: any;
  initialKPIs?: SuggestedKPI[]; // Optional pre-loaded KPIs (when resuming draft)
  onNext: (kpis: SuggestedKPI[]) => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
}

const KPIConfiguration: React.FC<KPIConfigurationProps> = ({
  templateName,
  templateStructure,
  initialKPIs,
  onNext,
  onBack,
  isLoading,
  error,
}) => {
  const [kpis, setKpis] = useState<SuggestedKPI[]>(initialKPIs || []);
  const [kpisLoading, setKpisLoading] = useState(false);
  const [kpisGenerated, setKpisGenerated] = useState(!!initialKPIs && initialKPIs.length > 0);
  const [editingKPI, setEditingKPI] = useState<SuggestedKPI | null>(null);
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const [editingKPIIndex, setEditingKPIIndex] = useState<number>(-1);

  // Auto-generate KPIs when component mounts (unless already provided)
  useEffect(() => {
    if (!kpisGenerated && !kpisLoading && (!initialKPIs || initialKPIs.length === 0)) {
      console.log('🤖 Auto-generating KPIs on mount...');
      handleGenerateKPIs();
    }
  }, []); // Empty dependency array = run once on mount

  const handleGenerateKPIs = async () => {
    setKpisLoading(true);
    try {
      const API_BASE = import.meta.env.PROD 
        ? 'https://web-production-d0043.up.railway.app/api'
        : (import.meta.env.VITE_API_URL || 'http://localhost:8001/api');
      
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      const response = await fetch(`${API_BASE}/kpis/suggest-for-wizard/`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          template_name: templateName,
          structure: templateStructure.structure,
        })
      });
      
      if (!response.ok) throw new Error('Failed to generate KPIs');
      
      const data = await response.json();
      setKpis(data.kpis || []);
      setKpisGenerated(true);
      console.log('✅ KPIs generated:', data.kpis);
    } catch (error: any) {
      console.error('Error generating KPIs:', error);
      // Set some default KPIs if generation fails
      setKpis([
        { name: 'Process Efficiency', description: 'Measures overall process improvement from audit actions' },
        { name: 'Compliance Score', description: 'Tracks compliance improvements and risk reduction' },
        { name: 'Quality Metrics', description: 'Monitors quality improvements resulting from audit recommendations' },
      ]);
      setKpisGenerated(true);
    } finally {
      setKpisLoading(false);
    }
  };

  const handleEditKPI = (index: number) => {
    setEditingKPI(kpis[index]);
    setEditingKPIIndex(index);
    setKpiDialogOpen(true);
  };

  const handleAddKPI = () => {
    setEditingKPI({ name: '', description: '' });
    setEditingKPIIndex(-1);
    setKpiDialogOpen(true);
  };

  const handleSaveKPI = () => {
    if (!editingKPI) return;
    
    if (editingKPIIndex >= 0) {
      // Update existing
      const updated = [...kpis];
      updated[editingKPIIndex] = editingKPI;
      setKpis(updated);
    } else {
      // Add new
      setKpis([...kpis, editingKPI]);
      if (!kpisGenerated) setKpisGenerated(true);
    }
    
    setKpiDialogOpen(false);
    setEditingKPI(null);
    setEditingKPIIndex(-1);
  };

  const handleDeleteKPI = (index: number) => {
    setKpis(prev => prev.filter((_, i) => i !== index));
  };

  const handleNext = () => {
    if (!kpisGenerated || kpis.length === 0) {
      alert('Please generate or add at least one KPI before continuing');
      return;
    }
    onNext(kpis);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <TrendingUp color="primary" fontSize="large" />
        <Box>
          <Typography variant="h5" gutterBottom>
            Configure KPIs
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Define which business metrics this review template will help improve
          </Typography>
        </Box>
      </Box>

      {/* KPI Section */}
      <Paper sx={{ p: 3, mb: 3, borderColor: !kpisGenerated ? 'warning.main' : 'divider', borderWidth: !kpisGenerated ? 2 : 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2, gap: 2 }}>
          <Box display="flex" flexDirection="column" flex={1} minWidth={0}>
            <Box display="flex" alignItems="center" gap={1} flexWrap="nowrap">
              <TrendingUp color="primary" />
              <Typography variant="h6" component="span" sx={{ whiteSpace: 'nowrap' }}>
                KPIs This Audit Influences
              </Typography>
              {!kpisGenerated && (
                <Box display="flex" alignItems="center" gap={0.5} sx={{ bgcolor: 'warning.light', px: 1, py: 0.25, borderRadius: 1, flexShrink: 0 }}>
                  <Warning sx={{ fontSize: 16, color: 'warning.dark' }} />
                  <Typography variant="caption" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                    Required
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography variant="body2" color="text.secondary">
              Define which business metrics this audit helps improve
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            {!kpisGenerated && (
              <Button
                variant="contained"
                size="small"
                startIcon={kpisLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                onClick={handleGenerateKPIs}
                disabled={kpisLoading || isLoading}
                data-testid="generate-kpis-button"
              >
                {kpisLoading ? 'Generating...' : 'Generate KPIs with AI'}
              </Button>
            )}
            {kpisGenerated && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={kpisLoading ? <CircularProgress size={16} color="inherit" /> : <AutoAwesome />}
                  onClick={handleGenerateKPIs}
                  disabled={kpisLoading || isLoading}
                >
                  Re-generate
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Add />}
                  onClick={handleAddKPI}
                  disabled={isLoading}
                >
                  Add KPI
                </Button>
              </>
            )}
          </Box>
        </Box>

        {kpisGenerated && kpis.length > 0 ? (
          <Box sx={{ mt: 2 }}>
            {kpis.map((kpi, index) => (
              <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box flex={1}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {kpi.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {kpi.description}
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <IconButton size="small" onClick={() => handleEditKPI(index)} disabled={isLoading}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteKPI(index)} disabled={isLoading}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : kpisGenerated ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No KPIs yet. Click "Add KPI" to manually add one.
          </Alert>
        ) : kpisLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" gap={2} sx={{ py: 4 }}>
            <CircularProgress />
            <Typography variant="body1" color="text.secondary">
              Generating KPIs with AI...
            </Typography>
          </Box>
        ) : (
          <Alert severity="warning" sx={{ mt: 2 }} icon={<TrendingUp />}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              KPI generation is required before saving
            </Typography>
            <Typography variant="body2">
              Click "Generate KPIs with AI" to automatically suggest relevant KPIs for this audit. This connects audit outcomes to business performance metrics.
            </Typography>
          </Alert>
        )}
      </Paper>


      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onBack}
          disabled={isLoading}
        >
          Back to Preview
        </Button>
        
        <Button
          variant="contained"
          size="small"
          onClick={handleNext}
          disabled={isLoading || !kpisGenerated || kpis.length === 0}
          startIcon={<CheckCircle />}
          data-testid="kpi-config-next-button"
        >
          Next: Client Access
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPI Edit Dialog */}
      <Dialog open={kpiDialogOpen} onClose={() => setKpiDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingKPIIndex >= 0 ? 'Edit KPI' : 'Add KPI'}
        </DialogTitle>
        <DialogContent>
          {editingKPI && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="KPI Name"
                value={editingKPI.name}
                onChange={(e) => setEditingKPI({ ...editingKPI, name: e.target.value })}
                placeholder="e.g., Forecast Accuracy (MAPE)"
                required
              />
              <TextField
                fullWidth
                multiline
                rows={3}
                label="KPI Description"
                value={editingKPI.description}
                onChange={(e) => setEditingKPI({ ...editingKPI, description: e.target.value })}
                placeholder="Describe what this KPI measures and how this audit helps improve it"
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKpiDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveKPI}
            disabled={!editingKPI?.name.trim() || !editingKPI?.description.trim()}
          >
            Save KPI
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KPIConfiguration;

